import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Crown, AlertTriangle, Undo2, Sliders, DollarSign, Lock, Flag, FileCheck2, Users, Radio, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import MFAPanel from "../components/MFAPanel";
import SovereignEye from "../components/SovereignEye";
import DueOuts from "../components/DueOuts";
import { useAuth } from "../context/AuthContext";

export default function DirectorPanel() {
    const { user } = useAuth();
    const [overview, setOverview] = useState(null);
    const [territories, setTerritories] = useState([]);
    const [reports, setReports] = useState([]);
    const [waitlist, setWaitlist] = useState(null);
    const [editingSplit, setEditingSplit] = useState(null);

    const load = async () => {
        const [ov, terr, rep, wl] = await Promise.all([
            api.get("/director/overview"),
            api.get("/territories"),
            api.get("/scout-reports"),
            api.get("/director/waitlist"),
        ]);
        setOverview(ov.data);
        setTerritories(terr.data);
        setReports(rep.data);
        setWaitlist(wl.data);
    };

    useEffect(() => { load(); }, []);

    const strike = async (code) => {
        try {
            await api.post(`/director/territories/${code}/strike`);
            toast.success(`Delivery issued on ${code}`);
            load();
        } catch (e) {
            toast.error("Delivery failed");
        }
    };

    const reclaim = async (code) => {
        try {
            await api.post(`/director/territories/${code}/reclaim`);
            toast.success(`${code} reclaimed to Available`);
            load();
        } catch (e) {
            toast.error("Reclaim failed");
        }
    };

    const saveSplits = async (partnerId, partner, platform, director, referral) => {
        try {
            await api.post(`/director/partners/${partnerId}/splits`, {
                partner_share: partner,
                platform_share: platform,
                director_share: director,
                referral_share: referral,
            });
            toast.success("Splits updated");
            setEditingSplit(null);
            load();
        } catch {
            toast.error("Failed to update splits");
        }
    };

    const approve = async (id) => {
        await api.post(`/scout-reports/${id}/approve`);
        toast.success("Report approved");
        load();
    };

    if (!overview) return <div className="text-muted-foreground">Summoning command data...</div>;

    const lockedTerritories = territories.filter((t) => t.status !== "available");

    return (
        <div className="space-y-8">
            <div className="fade-up">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-[hsl(45_85%_68%)]">
                    <Crown size={12} /> Director Master Control
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl mt-1 tracking-tighter">The Command Bridge</h1>
                <p className="text-muted-foreground mt-2 max-w-2xl">
                    Full visibility. Full authority. Adjust covenant splits, issue reclamation deliveries,
                    and approve fleet-wide intel.
                </p>
            </div>

            {/* Sovereign Eye + Due Outs · master visual + master checklist (Director directive 2026.02) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                    <SovereignEye mode="internal" height={520} />
                </div>
                <div className="lg:col-span-1">
                    <DueOuts user={user} overview={overview} />
                </div>
            </div>

            {/* Fleet stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatCard icon={DollarSign} label="Boarding Revenue" value={`$${overview.boarding_revenue.toLocaleString()}`} />
                <StatCard icon={DollarSign} label="Referral Pool" value={`$${(overview.total_referral_earnings || 0).toLocaleString()}`} />
                <StatCard icon={Lock} label="Locked" value={overview.fleet.locked} />
                <StatCard icon={AlertTriangle} label="Warning" value={overview.fleet.warning} />
                <StatCard icon={Flag} label="Available" value={overview.fleet.available} />
            </div>

            {/* MFA enrolment */}
            <MFAPanel />

            {/* Sovereign Star · Director-mintable BREAKING broadcasts */}
            <BroadcastConsole />

            {/* Waitlist by vertical */}
            {waitlist && (
                <div className="glass rounded-2xl p-6" data-testid="waitlist-panel">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="font-heading text-2xl">Waitlist by vertical</div>
                            <div className="text-xs text-muted-foreground">Top-of-funnel per tenant subdomain</div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Users size={14} /> {waitlist.total} total signups
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { key: "trade", label: "Trade", accent: "hsl(40 75% 55%)" },
                            { key: "sports", label: "Signal", accent: "hsl(153 50% 65%)" },
                            { key: "cyberops", label: "Tech Ops", accent: "hsl(280 50% 65%)" },
                            { key: "default", label: "Fleet", accent: "hsl(45 85% 68%)" },
                        ].map((t) => {
                            const data = waitlist.by_tenant[t.key] || { count: 0, recent: [] };
                            return (
                                <div key={t.key} className="p-5 rounded-xl bg-[hsl(151_55%_13%)] border border-[hsl(40_40%_25%/0.18)]" data-testid={`waitlist-tenant-${t.key}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: t.accent }}>{t.label}</div>
                                        <div className="font-heading text-3xl" style={{ color: t.accent }} data-testid={`waitlist-count-${t.key}`}>{data.count}</div>
                                    </div>
                                    <div className="mt-3 pt-3 border-t border-[hsl(40_40%_30%/0.12)] space-y-1 text-xs">
                                        {data.recent.length === 0 && (
                                            <div className="text-muted-foreground italic">No signups yet.</div>
                                        )}
                                        {data.recent.slice(0, 4).map((s) => (
                                            <div key={s.id} className="truncate" title={s.email}>
                                                <span className="text-foreground">{s.name}</span>
                                                <span className="text-muted-foreground"> · {s.email}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Partners */}
            <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="font-heading text-2xl">Silent partners</div>
                    <div className="text-xs text-muted-foreground">{overview.partners.length} boarded</div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-[hsl(40_40%_30%/0.18)]">
                            <tr>
                                <th className="py-3 pr-4">Agency</th>
                                <th className="py-3 pr-4">Territories</th>
                                <th className="py-3 pr-4">Fuel</th>
                                <th className="py-3 pr-4">Velocity</th>
                                <th className="py-3 pr-4">Yield</th>
                                <th className="py-3 pr-4">Split (P/Plat/Dir)</th>
                                <th className="py-3 pr-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {overview.partners.map((p) => (
                                <tr key={p.id} className="border-b border-[hsl(40_40%_30%/0.1)]" data-testid={`partner-row-${p.id}`}>
                                    <td className="py-3 pr-4">
                                        <div className="font-medium">{p.agency_name || p.name}</div>
                                        <div className="text-xs text-muted-foreground">{p.email}</div>
                                        {p.referred_by_name && (
                                            <div className="text-[10px] text-[hsl(45_85%_68%)] mt-0.5">via {p.referred_by_name}</div>
                                        )}
                                        <div className="text-[10px] text-muted-foreground font-mono mt-0.5">code: {p.referral_code}</div>
                                    </td>
                                    <td className="py-3 pr-4 font-mono">
                                        {p.territories?.length ? p.territories.join(", ") : <span className="text-muted-foreground">—</span>}
                                    </td>
                                    <td className="py-3 pr-4 font-mono">${p.total_spend?.toLocaleString()}</td>
                                    <td className="py-3 pr-4 font-mono">${p.total_revenue?.toLocaleString()}</td>
                                    <td className="py-3 pr-4 font-mono text-[hsl(45_85%_68%)]">${p["yield"]?.toLocaleString()}</td>
                                    <td className="py-3 pr-4">
                                        {editingSplit === p.id ? (
                                            <SplitEditor partner={p} onSave={saveSplits} onCancel={() => setEditingSplit(null)} />
                                        ) : (
                                            <div className="font-mono text-xs">
                                                {(p.partner_share * 100).toFixed(0)} / {(p.platform_share * 100).toFixed(0)} / {(p.director_share * 100).toFixed(0)}
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-3 pr-4">
                                        {editingSplit !== p.id && (
                                            <button
                                                onClick={() => setEditingSplit(p.id)}
                                                className="text-xs px-2 py-1 rounded border border-[hsl(40_50%_35%/0.35)] hover:bg-[hsl(151_55%_15%)] transition-colors inline-flex items-center gap-1"
                                                data-testid={`edit-split-${p.id}`}
                                            >
                                                <Sliders size={12} /> Splits
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {overview.partners.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="py-6 text-center text-muted-foreground">No partners boarded yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Territory controls */}
            <div className="glass rounded-2xl p-6">
                <div className="font-heading text-2xl mb-4">Territory controls</div>
                {lockedTerritories.length === 0 ? (
                    <div className="text-sm text-muted-foreground">All territories available. No strikes required.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {lockedTerritories.map((t) => (
                            <div key={t.state_code} className="p-4 rounded-lg bg-[hsl(151_55%_13%)] border border-[hsl(40_40%_25%/0.18)]">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{t.tier}</div>
                                        <div className="font-heading text-lg">{t.state_name} ({t.state_code})</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">{t.owner_name || "—"} · {t.owner_sector || "—"}</div>
                                    </div>
                                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${t.status === "warning" ? "bg-[hsl(350_65%_35%)] text-[hsl(0_0%_98%)]" : "bg-[hsl(151_55%_20%)] text-[hsl(151_25%_75%)]"}`}>{t.status}</span>
                                </div>
                                <div className="mt-3 flex items-center gap-2">
                                    {t.status === "locked" && (
                                        <button
                                            onClick={() => strike(t.state_code)}
                                            data-testid={`strike-${t.state_code}`}
                                            className="text-xs px-3 py-1.5 rounded-md bg-[hsl(350_65%_35%)] text-[hsl(0_0%_98%)] hover:bg-[hsl(350_70%_45%)] transition-colors inline-flex items-center gap-1"
                                        >
                                            <AlertTriangle size={12} /> Issue Delivery
                                        </button>
                                    )}
                                    <button
                                        onClick={() => reclaim(t.state_code)}
                                        data-testid={`reclaim-${t.state_code}`}
                                        className="text-xs px-3 py-1.5 rounded-md border border-[hsl(40_50%_35%/0.35)] hover:bg-[hsl(151_55%_15%)] transition-colors inline-flex items-center gap-1"
                                    >
                                        <Undo2 size={12} /> Reclaim
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Reports */}
            <div className="glass rounded-2xl p-6">
                <div className="font-heading text-2xl mb-4">Scout report review</div>
                <div className="space-y-3">
                    {reports.length === 0 && <div className="text-sm text-muted-foreground">No reports submitted.</div>}
                    {reports.map((r) => (
                        <div key={r.id} className="p-4 rounded-lg bg-[hsl(151_55%_13%)] border border-[hsl(40_40%_25%/0.18)]">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="text-sm font-medium">{r.title}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                        {r.category} · {r.state_code || "fleet-wide"} · by {r.user_name}
                                    </div>
                                </div>
                                {r.status === "approved" ? (
                                    <span className="text-[10px] uppercase tracking-wider text-[hsl(45_85%_68%)] inline-flex items-center gap-1"><FileCheck2 size={12} /> approved</span>
                                ) : (
                                    <button
                                        onClick={() => approve(r.id)}
                                        data-testid={`approve-${r.id}`}
                                        className="text-xs px-3 py-1 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors inline-flex items-center gap-1"
                                    >
                                        <FileCheck2 size={12} /> Approve
                                    </button>
                                )}
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">{r.content}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value }) {
    return (
        <div className="glass rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2 text-muted-foreground">
                <Icon size={14} />
                <span className="text-[10px] uppercase tracking-[0.3em]">{label}</span>
            </div>
            <div className="font-heading text-3xl">{value}</div>
        </div>
    );
}

function SplitEditor({ partner, onSave, onCancel }) {
    const [p, setP] = useState(partner.partner_share);
    const [pl, setPl] = useState(partner.platform_share);
    const [d, setD] = useState(partner.director_share);
    const [r, setR] = useState(partner.referral_share ?? 0.10);
    return (
        <div className="flex items-center gap-1 flex-wrap">
            <Input value={p} onChange={setP} testId={`input-partner-${partner.id}`} />
            <span className="text-muted-foreground">/</span>
            <Input value={pl} onChange={setPl} testId={`input-platform-${partner.id}`} />
            <span className="text-muted-foreground">/</span>
            <Input value={d} onChange={setD} testId={`input-director-${partner.id}`} />
            <span className="text-muted-foreground text-[10px] ml-1">ref</span>
            <Input value={r} onChange={setR} testId={`input-referral-${partner.id}`} />
            <button onClick={() => onSave(partner.id, p, pl, d, r)} className="text-[10px] px-2 py-1 rounded bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold" data-testid={`save-split-${partner.id}`}>
                save
            </button>
            <button onClick={onCancel} className="text-[10px] px-2 py-1 text-muted-foreground">cancel</button>
        </div>
    );
}

function Input({ value, onChange, testId }) {
    return (
        <input
            type="number"
            step="0.05"
            min="0"
            max="1"
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
            data-testid={testId}
            className="w-16 px-1.5 py-1 rounded bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[hsl(40_61%_53%)]"
        />
    );
}

function BroadcastConsole() {
    const [list, setList] = useState([]);
    const [form, setForm] = useState({
        headline: "BREAKING · DIRECTORATE",
        title: "",
        body: "",
        vessel: "DIRECTORATE",
        stat_label: "",
        stat_value: "",
    });
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await api.get("/director/sovereign-star/broadcasts");
            setList(data.broadcasts || []);
        } catch {
            // silent
        }
    };

    useEffect(() => { refresh(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (form.title.trim().length < 4) {
            toast.error("Title needs at least 4 chars.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/director/sovereign-star/broadcast", form);
            toast.success("Broadcast minted. Pinned to the runway.");
            setForm({ ...form, title: "", body: "", stat_label: "", stat_value: "" });
            refresh();
        } catch {
            toast.error("Mint failed.");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/director/sovereign-star/broadcasts/${id}`);
            toast.success("Broadcast retracted.");
            refresh();
        } catch {
            toast.error("Retract failed.");
        }
    };

    return (
        <div className="glass rounded-2xl p-6" data-testid="broadcast-console">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-[hsl(45_85%_68%)]">
                        <Radio size={11} /> Sovereign Star · Director-Mintable BREAKING
                    </div>
                    <div className="font-heading text-2xl mt-1">Broadcast console</div>
                </div>
                <span className="text-xs text-muted-foreground">{list.length} active</span>
            </div>

            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid="broadcast-form">
                <input
                    placeholder="Headline (e.g. BREAKING · FOUNDRY)"
                    value={form.headline}
                    onChange={(e) => setForm({ ...form, headline: e.target.value })}
                    data-testid="broadcast-headline"
                    className="px-3 py-2 rounded bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-sm focus:outline-none focus:border-[hsl(45_85%_68%)]"
                />
                <input
                    placeholder="Site (e.g. DIRECTORATE / FOUNDRY)"
                    value={form.vessel}
                    onChange={(e) => setForm({ ...form, vessel: e.target.value })}
                    data-testid="broadcast-vessel"
                    className="px-3 py-2 rounded bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-sm focus:outline-none focus:border-[hsl(45_85%_68%)]"
                />
                <input
                    placeholder="Title — the headline of the signal"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="broadcast-title"
                    className="sm:col-span-2 px-3 py-2 rounded bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-sm focus:outline-none focus:border-[hsl(45_85%_68%)]"
                />
                <textarea
                    placeholder="Body — short cinematic body copy"
                    rows={2}
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    data-testid="broadcast-body"
                    className="sm:col-span-2 px-3 py-2 rounded bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-sm focus:outline-none focus:border-[hsl(45_85%_68%)] resize-none"
                />
                <input
                    placeholder="Stat label (optional, e.g. Treasury Armor)"
                    value={form.stat_label}
                    onChange={(e) => setForm({ ...form, stat_label: e.target.value })}
                    data-testid="broadcast-stat-label"
                    className="px-3 py-2 rounded bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-sm focus:outline-none focus:border-[hsl(45_85%_68%)]"
                />
                <input
                    placeholder="Stat value (optional, e.g. 100%)"
                    value={form.stat_value}
                    onChange={(e) => setForm({ ...form, stat_value: e.target.value })}
                    data-testid="broadcast-stat-value"
                    className="px-3 py-2 rounded bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-sm focus:outline-none focus:border-[hsl(45_85%_68%)]"
                />
                <button
                    type="submit"
                    disabled={busy || form.title.trim().length < 4}
                    data-testid="broadcast-submit"
                    className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-40"
                >
                    <Send size={14} /> {busy ? "Minting..." : "Mint BREAKING signal"}
                </button>
            </form>

            {list.length > 0 && (
                <div className="mt-5 pt-5 border-t border-[hsl(40_40%_25%/0.25)] space-y-2" data-testid="broadcast-list">
                    {list.map((b) => (
                        <div key={b.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded bg-[hsl(151_55%_5%/0.6)] border border-[hsl(40_40%_25%/0.25)]">
                            <div className="min-w-0">
                                <div className="text-[9px] uppercase tracking-[0.35em] text-[hsl(45_85%_68%)] font-mono">{b.headline}</div>
                                <div className="text-sm font-medium truncate">{b.title}</div>
                                {b.stat_value && (
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        {b.stat_label}: <span className="font-mono text-[hsl(45_85%_75%)]">{b.stat_value}</span>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => remove(b.id)}
                                className="px-2 py-1.5 rounded text-xs text-muted-foreground hover:text-[hsl(350_75%_70%)] hover:bg-[hsl(350_30%_15%/0.4)] transition-colors flex items-center gap-1.5"
                                data-testid={`broadcast-delete-${b.id}`}
                            >
                                <Trash2 size={12} /> Retract
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
